import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  disabled?: boolean;
}

const sizes = {
  sm: { padding: '6px 12px', fontSize: 'var(--text-xs)', gap: 6, height: '30px' },
  md: { padding: '9px 16px', fontSize: 'var(--text-sm)', gap: 8, height: '38px' },
  lg: { padding: '12px 22px', fontSize: 'var(--text-base)', gap: 8, height: '46px' },
};

const variantStyle = (variant: string, disabled: boolean) => {
  if (disabled) {
    return {
      background: 'var(--bg-surface-hover)',
      color: 'var(--text-disabled)',
      border: '1px solid var(--border-subtle)',
    };
  }
  switch (variant) {
    case 'secondary':
      return {
        background: 'var(--bg-surface-raised)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-default)',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: 'var(--text-secondary)',
        border: '1px solid transparent',
      };
    case 'danger':
      return {
        background: 'var(--danger)',
        color: '#2a0a0a',
        border: '1px solid var(--danger)',
      };
    case 'primary':
    default:
      return {
        background: 'var(--accent)',
        color: 'var(--text-on-accent)',
        border: '1px solid var(--accent)',
      };
  }
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  disabled = false,
  onClick,
  style,
  type = 'button',
  ...props
}: ButtonProps) {
  const s = sizes[size] || sizes.md;
  const v = variantStyle(variant, disabled);

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: s.gap,
        padding: s.padding,
        fontSize: s.fontSize,
        fontFamily: 'var(--font-body)',
        fontWeight: 'var(--fw-medium)',
        borderRadius: 'var(--radius-md)',
        whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxSizing: 'border-box',
        transition:
          'background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)',
        ...v,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (variant === 'primary') e.currentTarget.style.background = 'var(--accent-hover)';
        else if (variant === 'secondary') e.currentTarget.style.background = 'var(--bg-surface-hover)';
        else if (variant === 'ghost') {
          e.currentTarget.style.background = 'var(--bg-surface-hover)';
          e.currentTarget.style.color = 'var(--text-primary)';
        }
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = v.background;
        if (variant === 'ghost') {
          e.currentTarget.style.color = 'var(--text-secondary)';
        }
      }}
      onFocus={(e) => {
        if (!disabled) e.currentTarget.style.boxShadow = 'var(--focus-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
}
